import type {
  PlatformProductCategory,
  SellerProductFiltersInput,
} from '~/graphql/graphql';
import { buildAndFilter, buildOrFilter } from '~/types';
import { textValueOf, type EditableFilterRow } from '~/types/filters';

export type ProductFilterRow = EditableFilterRow;

const PROPERTY_FIELD_PREFIX = 'properties.';

export function isPropertyField(field: string): boolean {
  return field.startsWith(PROPERTY_FIELD_PREFIX);
}

export function propertyKeyOf(field: string): string {
  return field.slice(PROPERTY_FIELD_PREFIX.length);
}

export function newProductFilterRowId(): string {
  return crypto.randomUUID();
}

type ProductFilterCriteria = Omit<ProductFilterRow, 'id'>;

export const STRING_OPERATORS = [
  'contains',
  'eq',
  'neq',
  'notContain',
] as const;
export const NUMBER_OPERATORS = [
  'eq',
  'neq',
  'gt',
  'lt',
  'gte',
  'lte',
] as const;
export const ENUM_OPERATORS = ['eq', 'neq'] as const;

const NUMERIC_FIELDS = [
  'pricePerUnit',
  'stock',
  'replenishmentRatePerWeek',
  'readyToShipDays',
];

const OPERATORS_BY_FIELD: Record<string, readonly string[]> = {
  visibility: ENUM_OPERATORS,
  productCode: STRING_OPERATORS,
  productName: STRING_OPERATORS,
  length: STRING_OPERATORS,
  pricePerUnit: NUMBER_OPERATORS,
  stock: NUMBER_OPERATORS,
  status: ENUM_OPERATORS,
  replenishmentRatePerWeek: NUMBER_OPERATORS,
  readyToShipDays: NUMBER_OPERATORS,
  dimensions: ENUM_OPERATORS,
};

export function operatorsForField(field: string): readonly string[] | null {
  if (isPropertyField(field)) return ENUM_OPERATORS;
  return OPERATORS_BY_FIELD[field] ?? null;
}

function normalizeValue(field: string, raw: unknown): string | number | null {
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed === '') return null;

    if (NUMERIC_FIELDS.includes(field)) {
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    }

    return trimmed;
  }

  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }

  return null;
}

function rowToClause(
  filter: ProductFilterCriteria,
  fields?: string[]
): SellerProductFiltersInput | null {
  if (
    fields &&
    !fields.includes(filter.field) &&
    !isPropertyField(filter.field)
  )
    return null;

  if (!operatorsForField(filter.field)?.includes(filter.operator)) return null;

  if (filter.field === 'dimensions') {
    const dimensionsValue = textValueOf(filter);
    if (dimensionsValue.trim() === '') return null;
    const [thickness, width] = dimensionsValue.split('X');
    const dimensions = {
      and: [
        { nominalThicknessInches: { eq: parseFloat(thickness) } },
        { nominalWidthInches: { eq: parseFloat(width) } },
      ],
    };
    return filter.operator === 'neq' ? { not: dimensions } : dimensions;
  }

  const value = normalizeValue(filter.field, filter.value);
  if (value === null) return null;

  if (isPropertyField(filter.field)) {
    const key = propertyKeyOf(filter.field).trim();
    if (!key) return null;
    return {
      properties: [{ key, operator: { [filter.operator]: value } }],
    };
  }

  return { [filter.field]: { [filter.operator]: value } };
}

export function formsClause(
  row: ProductFilterCriteria,
  fields?: string[]
): boolean {
  return rowToClause(row, fields) !== null;
}

/**
 * Converts filter panel rows into filter input clauses. Pass `fields` to drop
 * rows the target query does not accept, e.g. an Inventory-only field in a
 * stale Pricing preset; `properties.*` rows are always kept.
 */
export function filterRowsToClauses(
  rows: ProductFilterCriteria[],
  category?: PlatformProductCategory,
  fields?: string[]
): SellerProductFiltersInput[] {
  const groups: SellerProductFiltersInput[][] = [];
  let currentGroup: SellerProductFiltersInput[] = [];
  let startsOrGroup = false;
  for (const row of rows) {
    const clause = rowToClause(row, fields);
    if ((row.connector ?? 'and') === 'or') startsOrGroup = true;
    if (!clause) continue;
    if (startsOrGroup && currentGroup.length > 0) {
      groups.push(currentGroup);
      currentGroup = [];
    }
    startsOrGroup = false;
    currentGroup.push(clause);
  }
  if (currentGroup.length > 0) groups.push(currentGroup);

  const groupInputs = groups.map((group): SellerProductFiltersInput =>
    group.length === 1 ? group[0] : buildAndFilter(group)
  );

  const clauses: SellerProductFiltersInput[] = [];
  if (category) clauses.push({ category: { eq: category } });
  if (groupInputs.length === 1) clauses.push(groupInputs[0]);
  if (groupInputs.length > 1) clauses.push(buildOrFilter(groupInputs));
  return clauses;
}

export function searchTermClause(
  searchTerm: string
): SellerProductFiltersInput | undefined {
  const term = searchTerm.trim();
  if (!term) return undefined;
  return {
    or: [
      { productName: { contains: term } },
      { productCode: { contains: term } },
    ],
  };
}
